// ─────────────────────────────────────────────────────────────
// InventoryService — inventory 테이블 책임 격리
// 인벤튜라 프로의 가장 빈번한 도메인 (재고 마스터)
// 시리얼 단위 CRUD, optimistic lock 업데이트, 검색/자동완성, bulk insert
//
// 복잡한 페이지 query(검색+필터+정렬 조합)는 App.js에 raw로 남김 (UI 로직 강결합)
//
// ⚠ inventory.status는 DB CHECK constraint로 제한됨:
//     허용 값: 'in_stock' | 'shipped' | 'unsellable' | 'reserved'
//
//   히스토리:
//     v7.55에서 'reserved' 도입 시 DB 제약 갱신을 누락하여 모든 방문수령 처리가 실패함.
//     2026-06-30에 다음 SQL로 영구 해결:
//       ALTER TABLE inventory DROP CONSTRAINT inventory_status_check;
//       ALTER TABLE inventory ADD CONSTRAINT inventory_status_check
//         CHECK (status IN ('in_stock', 'shipped', 'unsellable', 'reserved'));
//
//   ★ 새 status 값을 추가하려면 반드시 같은 패턴으로 ALTER TABLE 해서 제약 갱신.
//     안 그러면 update가 silent하게 거부되어 .select() 반환이 빈 배열 → conflict로 오인됨.
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

// v7.126: 로컬 타임존 기준 (기존 toISOString은 UTC라 한국 오전 9시 전 어제 날짜 반환 버그)
const localToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const InventoryService = {
  // ─── 조회: 단건 ─────────────────────────────────────────────
  // 시리얼 1개 조회 (전체 컬럼)
  async findBySerial(serial) {
    if (!serial) return null;
    const { data } = await supabase.from('inventory')
      .select('*')
      .eq('serial_number', serial)
      .maybeSingle();
    return data;
  },

  // 시리얼 1개 조회 (지정 컬럼만) — status 검증 등 경량 조회용
  async findBySerialFields(serial, fields) {
    if (!serial) return null;
    const { data } = await supabase.from('inventory')
      .select(fields)
      .eq('serial_number', serial)
      .maybeSingle();
    return data;
  },

  // ─── 조회: 카운트 ───────────────────────────────────────────
  // 전체 시리얼 카운트 + 연결 상태 — head only (가장 빠름)
  // 앱 시작 시 connect 상태 확인용으로도 사용됨
  async countAll() {
    const { count, error } = await supabase.from('inventory')
      .select('serial_number', { count: 'exact', head: true });
    return { ok: !error, count: count || 0 };
  },

  // 특정 product_name 카운트 (필터 옵션 있음)
  async countByProduct(productName, { status } = {}) {
    let q = supabase.from('inventory').select('id', { count: 'exact', head: true })
      .eq('product_name', productName);
    if (status) q = q.eq('status', status);
    const { count } = await q;
    return count || 0;
  },

  // in_stock 카운트 (가장 빈번하게 쓰이는 케이스) — shorthand
  async countInStock(productName) {
    return InventoryService.countByProduct(productName, { status: 'in_stock' });
  },

  // v7.378: 여러 제품의 재고 수량을 한 번에 — { [product_name]: 수량 }
  //   ⚠ 제품 하나당 countInStock 을 부르면 배송시트지 한 페이지에 수십 번 왕복한다.
  //     .in() 한 방으로 받아서 클라이언트에서 센다.
  //   ⚠ 결과에 없는 이름은 [재고 0] 이다. 호출한 이름 전부를 0으로 미리 깔아 두는 이유 —
  //     undefined 와 0 을 구분하지 못하면 화면에서 "로딩 중"과 "재고없음"이 섞인다.
  async countInStockByNames(names, { pageSize = 1000 } = {}) {
    const list = [...new Set((names || []).filter(Boolean))];
    if (list.length === 0) return {};
    const out = {};
    list.forEach(n => { out[n] = 0; });
    // 이름이 많으면 .in() URL 이 길어져 414 가 난다 → 나눠 보낸다
    const CHUNK = 80;
    for (let i = 0; i < list.length; i += CHUNK) {
      const part = list.slice(i, i + CHUNK);
      const { data, error } = await supabase.from('inventory')
        .select('product_name')
        .eq('status', 'in_stock')
        .in('product_name', part)
        .limit(pageSize * 10);
      if (error) return null;
      (data || []).forEach(r => { out[r.product_name] = (out[r.product_name] || 0) + 1; });
    }
    return out;
  },

  // ─── 조회: 페이지/일괄 ──────────────────────────────────────
  // 페이지네이션 헬퍼 — 전체 테이블 순회 (통계/캐시용)
  // returns: 모든 row 누적된 배열
  async fetchAll(fields, { pageSize = 1000, safeLimit = 50000 } = {}) {
    const all = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase.from('inventory').select(fields).range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      all.push(...data);
      if (data.length < pageSize) break;
      from += pageSize;
      if (from > safeLimit) break; // 안전 상한
    }
    return all;
  },

  // 시리얼 단위 캐시용 (Set 반환) — 입고 모달 진입 시 호출
  async fetchSerialCache({ pageSize = 1000, safeLimit = 50000, cancelCheck } = {}) {
    const all = new Set();
    let from = 0;
    while (true) {
      const { data, error } = await supabase.from('inventory').select('serial_number').range(from, from + pageSize - 1);
      if (error || !data || (cancelCheck && cancelCheck())) break;
      data.forEach(r => r.serial_number && all.add(r.serial_number));
      if (data.length < pageSize) break;
      from += pageSize;
      if (from > safeLimit) break;
    }
    return all;
  },

  // v7.10: 가용재고 현황 요약 — 제품별 in_stock 카운트
  // returns: { [product_name]: count }
  async getInStockProductCounts({ pageSize = 1000, safeLimit = 50000 } = {}) {
    // v7.22: counts + stockTypeCounts 같이 반환 (이전: counts만)
    //  - counts: {product_name: in_stock_count}
    //  - stockTypeCounts: {product_name: {신품, 리페킹, 조립출고, 기타}}
    const counts = {};
    const stockTypeCounts = {};
    let from = 0;
    while (true) {
      const { data, error } = await supabase.from('inventory')
        .select('product_name, stock_type')
        .eq('status', 'in_stock')
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      data.forEach(r => {
        if (!r.product_name) return;
        counts[r.product_name] = (counts[r.product_name] || 0) + 1;
        // v7.184: AS 필드 추가 — 기존엔 '기타'로 묻혔음
        if (!stockTypeCounts[r.product_name]) stockTypeCounts[r.product_name] = { 신품: 0, AS: 0, 리페킹: 0, 조립출고: 0, 기타: 0 };
        const t = r.stock_type || '신품';
        if (t === '신품' || t === 'AS' || t === '리페킹' || t === '조립출고') stockTypeCounts[r.product_name][t]++;
        else stockTypeCounts[r.product_name]['기타']++;
      });
      if (data.length < pageSize) break;
      from += pageSize;
      if (from > safeLimit) break;
    }
    // 하위 호환: 객체로 반환하지만 Proxy로 기존처럼 직접 접근(counts[name])도 동작하게
    // → 단순화: { counts, stockTypeCounts } 직접 사용으로 변경 (loadStockDashboard도 같이 수정됨)
    return { counts, stockTypeCounts };
  },

  // v7.17: 제품명 → 카테고리 매핑 (inventory.category 기준)
  // 같은 product_name의 시리얼들은 모두 같은 카테고리라고 가정 → 첫 번째 row의 카테고리 사용
  async getProductCategoryMap({ pageSize = 1000, safeLimit = 50000 } = {}) {
    const map = {};
    let from = 0;
    while (true) {
      const { data, error } = await supabase.from('inventory')
        .select('product_name, category')
        .not('category', 'is', null)
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      data.forEach(r => {
        if (r.product_name && !map[r.product_name]) map[r.product_name] = r.category;
      });
      if (data.length < pageSize) break;
      from += pageSize;
      if (from > safeLimit) break;
    }
    return map;
  },

  // ─── 조회: 검색/자동완성 ────────────────────────────────────
  // in_stock 상태 시리얼 자동완성 (특정 product_name 안에서)
  // 빈 쿼리면 전체, 쿼리 있으면 ilike
  async searchInStockSerials({ productName, query = '', limit = 20 }) {
    if (!productName) return [];
    let q = supabase.from('inventory')
      .select('serial_number')
      .eq('product_name', productName)
      .eq('status', 'in_stock')
      .order('serial_number')
      .limit(limit);
    if (query && query.length > 0) q = q.ilike('serial_number', `%${query}%`);
    const { data } = await q;
    return data || [];
  },

  // 제품 검색 + in_stock 집계용 — UI에서 그룹핑 가공
  // 출고 모달, 빠른 추가용
  async searchInStockProducts(query, { limit = 500 } = {}) {
    if (!query) return [];
    const { data } = await supabase.from('inventory')
      .select('product_name,category,stock_type')
      .eq('status', 'in_stock')
      .ilike('product_name', `%${query}%`)
      .limit(limit);
    return data || [];
  },

  // 모델 검색 (status 무관) — 모델명 변경 모달용
  async searchProductsForRename(query, { limit = 300 } = {}) {
    if (!query) return [];
    const { data } = await supabase.from('inventory')
      .select('product_name,category,stock_type,status')
      .ilike('product_name', `%${query}%`)
      .limit(limit);
    return data || [];
  },

  // ─── 조회: 시리얼 다건 ─────────────────────────────────────
  // 시리얼 배열 → 이미 등록된 것만 Set으로 (입고 시 중복 체크)
  async findExistingSerials(serials) {
    if (!serials || serials.length === 0) return new Set();
    const { data } = await supabase.from('inventory')
      .select('serial_number')
      .in('serial_number', serials);
    return new Set((data || []).map(r => r.serial_number));
  },

  // ─── 업데이트: 단건 ─────────────────────────────────────────
  // 시리얼 단건 업데이트 (락 없음) — memo/status/임의 필드
  async updateBySerial(serial, patch) {
    const { error } = await supabase.from('inventory').update(patch).eq('serial_number', serial);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // 시리얼 PK 변경 (오타 수정용) — 중복 체크 후 진행
  // returns: { ok, msg }
  async renameSerial(oldSerial, newSerial) {
    if (!oldSerial || !newSerial) return { ok: false, msg: '시리얼이 비어있습니다.' };
    if (oldSerial === newSerial) return { ok: true };
    // 새 시리얼 중복 체크
    const existing = await InventoryService.findBySerial(newSerial);
    if (existing) return { ok: false, msg: `시리얼 "${newSerial}"이 이미 존재합니다.` };
    const { error } = await supabase.from('inventory')
      .update({ serial_number: newSerial })
      .eq('serial_number', oldSerial);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // 출고 처리 — optimistic lock (status='in_stock' 또는 'ship_hold' 일 때만 shipped로)
  // returns: { ok: true } if updated, { ok: false, conflict: true } if 이미 다른 상태
  // v7.424: 담기 시 ship_hold 로 예약되므로 커밋 시 ship_hold 도 허용해야 출고가 된다.
  //   (배포 직후 아직 in_stock 인 옛 pending 도 함께 커밋되도록 둘 다 허용)
  async shipBySerial(serial, { dealerName, note = '', saleDate }) {
    const { data } = await supabase.from('inventory').update({
      status: 'shipped',
      sale_date: saleDate || localToday(), // v7.126: UTC 버그 방지
      dealer_name: dealerName,
      note,
    })
    .eq('serial_number', serial)
    .in('status', ['in_stock', 'ship_hold'])
    .select('serial_number');
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // v7.55: 방문수령 예약 — in_stock(또는 v7.424 ship_hold) → reserved (시리얼 미리 찍기)
  async reserveBySerial(serial, { dealerName = null, note = '' } = {}) {
    const { data } = await supabase.from('inventory').update({
      status: 'reserved',
      dealer_name: dealerName,
      note,
    })
    .eq('serial_number', serial)
    .in('status', ['in_stock', 'ship_hold'])
    .select('serial_number');
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // ─── v7.424: 출고 대기 예약(ship_hold) 생명주기 ─────────────────
  //   배송건에 시리얼을 담는 순간 in_stock → ship_hold 로 잠근다.
  //   이렇게 하면 in_stock 만 보는 재고 후보/카운트에서 자동으로 빠져
  //   다른 배송건에 중복으로 담기지 않는다(이중 할당 방지).
  //   ⚠ reserved(방문수령)와 완전히 분리된 상태값이라 pickup 예약을 건드리지 않는다.
  async holdBySerial(serial, { reqId = '' } = {}) {
    // ⚠ note 는 건드리지 않는다 — 입고 audit note 보존. 식별은 status='ship_hold' 로 충분하고,
    //   고아 회복은 ship_pending_serials 기준(reconcileOrphanHolds)이라 마커가 불필요하다.
    // v7.425: error 를 반드시 구분해서 돌려준다.
    //   v7.424 는 error 를 안 받아 CHECK 제약 위반(= 마이그레이션 미실행)까지 'conflict' 로 뭉개
    //   "방금 다른 곳에서 담겼습니다" 라는 엉뚱한 메시지가 떴다(사용자 리포트 1번).
    const { data, error } = await supabase.from('inventory').update({
      status: 'ship_hold',
    })
    .eq('serial_number', serial)
    .eq('status', 'in_stock')          // in_stock 일 때만 — 이미 담겼거나 출고된 건 conflict
    .select('serial_number');
    if (error) return { ok: false, dbError: true, msg: error.message || String(error) };
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // ship_hold → in_stock (배송건에서 빼거나 취소 시)
  async releaseHoldBySerial(serial) {
    const { data } = await supabase.from('inventory').update({
      status: 'in_stock',
    })
    .eq('serial_number', serial)
    .eq('status', 'ship_hold')         // ship_hold 만 — reserved(방문수령)/shipped 는 절대 안 건드림
    .select('serial_number');
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // 앱 시작 시 정합성 회복 — ship_pending_serials 에 대응 행이 없는 '고아 ship_hold' 를 in_stock 으로 되돌린다.
  //   어떤 해제 경로를 놓쳐 시리얼이 ship_hold 로 묶여 재고에서 사라지는 것을 self-heal 로 막는다.
  //   ⚠ ship_pending_serials 가 '담긴 시리얼' 의 단일 진실원. 거기 없는 ship_hold = 버려진 예약.
  async reconcileOrphanHolds() {
    const { data: held, error: e1 } = await supabase.from('inventory')
      .select('serial_number').eq('status', 'ship_hold');
    if (e1 || !held || held.length === 0) return { ok: true, released: 0 };
    const { data: pend } = await supabase.from('ship_pending_serials').select('serial');
    const pendSet = new Set((pend || []).map(r => r.serial));
    const orphans = held.map(r => r.serial_number).filter(sn => !pendSet.has(sn));
    if (orphans.length === 0) return { ok: true, released: 0 };
    // 배치로 되돌림 (ship_hold 인 것만 — 그 사이 커밋돼 shipped 된 건 건드리지 않음)
    const { data: upd, error: e2 } = await supabase.from('inventory')
      .update({ status: 'in_stock' })
      .in('serial_number', orphans)
      .eq('status', 'ship_hold')
      .select('serial_number');
    if (e2) return { ok: false, msg: e2.message, released: 0 };
    return { ok: true, released: (upd || []).length };
  },

  // v7.55: 방문수령 완료 — reserved → shipped (고객이 가져갔음)
  async completePickupBySerial(serial, { saleDate } = {}) {
    const { data } = await supabase.from('inventory').update({
      status: 'shipped',
      sale_date: saleDate || localToday(), // v7.126: UTC 버그 방지
    })
    .eq('serial_number', serial)
    .eq('status', 'reserved')
    .select('serial_number');
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // v7.55: 예약 해제 — reserved → in_stock (취소/환불 시 자동 복귀용)
  async releaseReservation(serial, { note = '' } = {}) {
    const { data } = await supabase.from('inventory').update({
      status: 'in_stock',
      sale_date: null,
      dealer_name: null,
      note,
    })
    .eq('serial_number', serial)
    .eq('status', 'reserved')
    .select('serial_number');
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // 환불/삭제 복귀 — v7.55: shipped/reserved, v7.424: ship_hold 도 in_stock으로 복귀
  async restoreToStock(serial, { note = '' } = {}) {
    const { data } = await supabase.from('inventory').update({
      status: 'in_stock',
      sale_date: null,
      dealer_name: null,
      note,
    })
    .eq('serial_number', serial)
    .in('status', ['shipped', 'reserved', 'ship_hold'])
    .select('serial_number');
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // 수리 완료 복귀 — optimistic lock (status='unsellable' → 'in_stock')
  async repairCompleteBySerial(serial, { note = '' } = {}) {
    const { data, error } = await supabase.from('inventory').update({
      status: 'in_stock',
      note,
    })
    .eq('serial_number', serial)
    .eq('status', 'unsellable')
    .select('serial_number');
    if (error) return { ok: false, msg: error.message };
    if (!data || data.length === 0) return { ok: false, conflict: true };
    return { ok: true };
  },

  // 판매불가 마킹 (RMA 시) — 락 없음
  async markUnsellable(serial, { note = '' } = {}) {
    return InventoryService.updateBySerial(serial, { status: 'unsellable', note });
  },

  // ─── 업데이트: 다건 ─────────────────────────────────────────
  // product_name 일괄 변경 (카탈로그 rename 시 외래키 동기화)
  async renameProductName(oldName, newName) {
    const { error } = await supabase.from('inventory')
      .update({ product_name: newName })
      .eq('product_name', oldName);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // ─── INSERT ─────────────────────────────────────────────────
  // 신규 입고 (bulk)
  async bulkInsert(rows) {
    if (!rows || rows.length === 0) return { ok: true };
    const { error } = await supabase.from('inventory').insert(rows);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // v7.15: 시리얼 단건 삭제 — 입고 시리얼 stepper 용
  // 안전장치: in_stock 상태일 때만 삭제 허용 (출고된 시리얼은 거부)
  async deleteBySerial(serial) {
    const { data: row, error: fetchErr } = await supabase
      .from('inventory')
      .select('status')
      .eq('serial_number', serial)
      .single();
    if (fetchErr || !row) return { ok: false, msg: `시리얼 찾을 수 없음: ${serial}` };
    if (row.status !== 'in_stock') {
      return { ok: false, msg: `이미 출고/처리된 시리얼은 삭제할 수 없습니다 (status: ${row.status})` };
    }
    const { error } = await supabase.from('inventory').delete().eq('serial_number', serial).eq('status', 'in_stock');
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },
};
